// src/app/auth/login/Login.js
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import { makeStyles } from '@material-ui/core/styles';
import { darken } from '@material-ui/core/styles/colorManipulator';
import clsx from 'clsx';
import { motion } from 'framer-motion';
import { yupResolver } from '@hookform/resolvers/yup';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import Icon from '@material-ui/core/Icon';
import IconButton from '@material-ui/core/IconButton';
import InputAdornment from '@material-ui/core/InputAdornment';
import Typography from '@material-ui/core/Typography';
import Divider from '@material-ui/core/Divider';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { submitLogin, submitProjectChoice, submitGroupChoice, submitRoleChoice, resetLoginState } from 'app/auth/store/loginSlice';
import { logoutUserSilent } from 'app/auth/store/userSlice';
import { Link } from 'react-router-dom';
import * as yup from 'yup';
import _ from '@lodash';
import { useTranslation } from 'react-i18next';
import ChooseProjectPanel from './ChooseProjectPanel';
import ChooseGroupPanel from './ChooseGroupPanel';
import ChooseRolePanel from './ChooseRolePanel';
import nBossConnection from 'modules/base/service/nBossConnection';
import { AUTH_FLOW } from 'app/auth/authFlowConfig';

const NO_GROUP = {
  title: 'No group is assigned to your account',
  content: 'Your account has no group assigned. Please contact the Advanced Modelling Team.',
  emailrecipient: 'advancedmodellingteam@mol.hu',
  emailsubject: 'Access request',
  emailcontent:
    'Dear Advanced Modelling Team,\n\nI would like to request access.\n\nName: {name}\nPurpose of use: {purpose}\nEmail: {email}\nPhone: {phone}\n\nKind regards,\n{name}'
};

const NO_ROLE = {
  title: 'No role is assigned to your account',
  content: 'Your account has no role assigned. Please contact the Advanced Modelling Team.',
  emailrecipient: 'advancedmodellingteam@mol.hu',
  emailsubject: 'Access request',
  emailcontent:
    'Dear Advanced Modelling Team,\n\nI would like to request access.\n\nName: {name}\nPurpose of use: {purpose}\nEmail: {email}\nPhone: {phone}\n\nKind regards,\n{name}'
};

const NO_PROJECT = {
  title: 'No project is assigned to your account',
  content: 'Your account has no project assigned. Please contact an administrator.',
  emailrecipient: 'advancedmodellingteam@mol.hu',
  emailsubject: 'Access request',
  emailcontent:
    'Dear Advanced Modelling Team,\n\nI would like to request project access.\n\nName: {name}\nPurpose of use: {purpose}\nEmail: {email}\nPhone: {phone}\n\nKind regards,\n{name}'
};

const useStyles = makeStyles((theme) => ({
  root: {
    background: `linear-gradient(to right, ${theme.palette.primary.dark} 0%, ${darken(theme.palette.primary.dark, 0.5)} 100%)`,
    color: theme.palette.primary.contrastText
  },
  leftSection: {}
}));

const defaultValues = { email: '', password: '' };

const _mailto = ({ to, subject, body }) =>
  `mailto:${encodeURIComponent(String(to || ''))}?subject=${encodeURIComponent(String(subject || ''))}&body=${encodeURIComponent(String(body || ''))}`;

const uniq = (arr) => Array.from(new Set((arr || []).filter(Boolean)));

function Login() {
  const classes = useStyles();
  const { t } = useTranslation(['auth', 'validation']);
  const dispatch = useDispatch();

  const SAML_LOGIN = false;
  const SAML_REDIRECT_URL = String(nBossConnection.baseUrl || '').replace(/\/+$/, '') + '/samlUserLogin/redirect/';

  const login = useSelector(({ auth }) => auth.login);
  const inProgress = !!login?.inProgress;

  const isChoosing =
    login?.nextStep === 'chooseProject' ||
    login?.nextStep === 'chooseGroup' ||
    login?.nextStep === 'chooseRole';

  const [choiceFlow, setChoiceFlow] = useState(false);

  const schema = yup.object().shape({
    email: yup.string().email(t('validation:EMAIL_IS_NOT_VALID')).required(t('validation:EMAIL_IS_REQUIRED')),
    password: yup.string().required(t('validation:PASSWORD_REQUIRED')).min(5, t('validation:PASSWORD_IS_TOO_SHORT'))
  });

  const { control, setValue, formState, handleSubmit, reset, trigger, setError } = useForm({
    mode: 'onChange',
    defaultValues,
    resolver: yupResolver(schema)
  });

  const { isValid, dirtyFields, errors } = formState;
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    setValue('email', '', { shouldDirty: true, shouldValidate: true });
    setValue('password', '', { shouldDirty: true, shouldValidate: true });
  }, [reset, setValue, trigger]);

  useEffect(() => {
    if (Array.isArray(login?.errors)) login.errors.forEach((error) => setError(error.type, { type: 'manual', message: error.message }));
  }, [login?.errors, setError]);

  const messageConfig = useMemo(() => {
    if (login?.nextStep === 'noProject') return NO_PROJECT;
    if (login?.nextStep === 'noGroup') return NO_GROUP;
    if (login?.nextStep === 'noRole') return NO_ROLE;
    return null;
  }, [login?.nextStep]);

  useEffect(() => {
    if (isChoosing) {
      if (!choiceFlow) setChoiceFlow(true);
      return;
    }
    if (!inProgress && !login?.isLoggedIn && !login?.success && !messageConfig) {
      if (choiceFlow) setChoiceFlow(false);
    }
  }, [isChoosing, inProgress, login?.isLoggedIn, login?.success, messageConfig, choiceFlow]);

  function onSubmit(model) {
    dispatch(submitLogin(model));
  }

  const handleSelectProject = (project) => {
    setChoiceFlow(true);
    dispatch(submitProjectChoice(project.id));
  };

  const handleSelectGroup = (group) => {
    setChoiceFlow(true);
    dispatch(submitGroupChoice(group.id));
  };

  const handleSelectRole = (role) => {
    setChoiceFlow(true);
    dispatch(submitRoleChoice(role));
  };

  const handleRefreshProjects = () => console.log('[chooseProject] refresh');
  const handleRefreshGroups = () => console.log('[chooseGroup] refresh');
  const handleRefreshRoles = () => console.log('[chooseRole] refresh');

  const projects = login?.projects ?? login?.meta?.projects ?? login?.client?.projects ?? [];
  const groups = login?.meta?.groups ?? login?.client?.userGroups ?? [];

  const roles = uniq([
    ...(Array.isArray(login?.roles) ? login.roles : []),
    ...(Array.isArray(login?.meta?.roles) ? login.meta.roles : []),
    ...(Array.isArray(login?.client?.roles) ? login.client.roles : [])
  ]);

  const handleSamlLogin = () => window.location.assign(SAML_REDIRECT_URL);

  const hardDisabled = !!login?.success || !!login?.isLoggedIn;
  const backVisible = isChoosing || choiceFlow;
  const backDisabled = inProgress || hardDisabled;
  const authButtonsDisabled = inProgress || hardDisabled || choiceFlow;

  const mailtoHref = useMemo(() => {
    if (!messageConfig) return '';
    const u = login?.client || {};
    const body = String(messageConfig.emailcontent || '')
      .replaceAll('{email}', String(u.email || ''))
      .replaceAll('{name}', String(u.name || ''))
      .replaceAll('{purpose}', '')
      .replaceAll('{phone}', '');
    return _mailto({ to: messageConfig.emailrecipient, subject: messageConfig.emailsubject, body });
  }, [messageConfig, login?.client]);

  const backToLogin = async () => {
    setChoiceFlow(false);
    try {
      await dispatch(logoutUserSilent());
    } catch (e) {}
    dispatch(resetLoginState());
    reset(defaultValues);
    setValue('email', '', { shouldDirty: true, shouldValidate: true });
    setValue('password', '', { shouldDirty: true, shouldValidate: true });
  };

  const onMailto = () => {
    if (mailtoHref) window.open(mailtoHref, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className={clsx(classes.root, 'flex flex-col flex-auto items-center justify-center flex-shrink-0 p-16 md:p-24')}>
      <motion.div initial={{ opacity: 0, scale: 0.6 }} animate={{ opacity: 1, scale: 1 }} className="flex min-w-320 max-w-400 md:max-w-3xl rounded-20 shadow-2xl overflow-hidden">
        <Card className={clsx(classes.leftSection, 'flex flex-col w-full max-w-sm items-center justify-center shadow-0')} square>
          <CardContent className="flex flex-col items-center justify-center w-full py-24 max-w-320">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1, transition: { delay: 0.2 } }}>
              <div className="flex flex-col items-center pt-8 pb-32">
                <img className="w-9/12 h-auto" src="assets/images/logos/sip_logo.png" alt="nBoss Admin" />
              </div>
            </motion.div>

            <div className="w-full">
              {messageConfig ? (
                <div className="w-full flex flex-col gap-12 text-center">
                  <Typography className="text-18 font-semibold">{messageConfig.title}</Typography>
                  <Typography className="text-13 opacity-80">{messageConfig.content}</Typography>
                  <Divider className="my-8" />
                  <div className="w-full flex flex-col gap-8">
                    <Button type="button" variant="contained" color="primary" className="w-full" disabled={inProgress} onClick={backToLogin}>
                      Back to login
                    </Button>
                    <Button type="button" variant="contained" color="secondary" className="w-full" disabled={inProgress} onClick={onMailto}>
                      Contact admin
                    </Button>
                  </div>
                </div>
              ) : (
                <form className="flex flex-col justify-center w-full" onSubmit={handleSubmit(onSubmit)}>
                  <Controller
                    name="email"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                             disabled={login?.nextStep}
                        autoComplete="username"
                        className="mb-16"
                        type="text"
                        error={!!errors.email}
                        helperText={errors?.email?.message}
                        label={t('EMAIL')}
                        InputProps={{
                          className: 'pr-4',
                          endAdornment: (
                            <InputAdornment position="end" className="ml-4">
                              <Icon className="text-20 mx-12" color="action">
                                email
                              </Icon>
                            </InputAdornment>
                          )
                        }}
                        variant="outlined"
                        required
                      />
                    )}
                  />

                  <Controller
                    name="password"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        disabled={login?.nextStep}
                        autoComplete="current-password"
                        className="mb-16"
                        label={t('PASSWORD')}
                        type="password"
                        error={!!errors.password}
                        helperText={errors?.password?.message}
                        variant="outlined"
                        InputProps={{
                          className: 'pr-4',
                          type: showPassword ? 'text' : 'password',
                          endAdornment: (
                            <InputAdornment position="end" className="ml-4">
                              <IconButton onClick={() => setShowPassword(!showPassword)}>
                                <Icon className="text-20" color="action">
                                  {showPassword ? 'visibility' : 'visibility_off'}
                                </Icon>
                              </IconButton>
                            </InputAdornment>
                          )
                        }}
                        required
                      />
                    )}
                  />

                  <div className="w-full flex flex-col gap-8 mt-8">
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      className="w-full"
                      aria-label="LOG IN"
                      disabled={authButtonsDisabled || _.isEmpty(dirtyFields) || !isValid}
                      value="legacy"
                    >
                      {t('LOGIN')}
                    </Button>

                    {SAML_LOGIN && (
                      <Button type="button" variant="contained" color="secondary" className="w-full" aria-label="SAML LOGIN" disabled={authButtonsDisabled} onClick={handleSamlLogin}>
                        SSO login
                      </Button>
                    )}

                    {backVisible && login?.nextStep && (
                      <Button type="button" variant="contained" color="primary" className="w-full" disabled={backDisabled} onClick={backToLogin}>
                        Cancel
                      </Button>
                    )}
                  </div>

                  {AUTH_FLOW.requireProject && login?.nextStep === 'chooseProject' && (
                    <ChooseProjectPanel projects={projects} onSelect={handleSelectProject} onRefresh={handleRefreshProjects} disabled={inProgress} />
                  )}

                  {AUTH_FLOW.requireGroup && login?.nextStep === 'chooseGroup' && (
                    <ChooseGroupPanel groups={groups} onSelect={handleSelectGroup} onRefresh={handleRefreshGroups} disabled={inProgress} />
                  )}

                  {AUTH_FLOW.requireRole && login?.nextStep === 'chooseRole' && (
                    <ChooseRolePanel roles={roles} onSelect={handleSelectRole} onRefresh={handleRefreshRoles} disabled={inProgress} />
                  )}
                </form>
              )}
            </div>
          </CardContent>

          {!messageConfig && (
            <div className="flex flex-col items-center justify-center pb-24">
              <div>
                <span className="font-normal">{t('LOST_YOUR_PASSWORD')} </span>
                <Link color="secondary" className="font-semibold" to="/password-request">
                  {t('REQUEST_A_NEW_PASSWORD')}
                </Link>
              </div>
            </div>
          )}
        </Card>
      </motion.div>
    </div>
  );
}

export default Login;
